import { type ReactNode, useId } from "react";

import { cn, tv } from "../foundations/cn.js";
import { Card } from "./Card.js";
import { Text } from "./Text.js";

const panelStyles = tv({
  base: "flex flex-col",
  variants: {
    size: {
      /** A panel that is most of the page: a settings panel, a form. */
      md: "gap-5 p-5 md:p-8",
      /** One of several stacked or side by side: a builder's steps, a summary beside a list. */
      sm: "min-w-0 gap-4 p-4 md:p-5",
    },
  },
  defaultVariants: { size: "md" },
});

const HEADING = { md: "heading-h1", sm: "heading-h2" } as const;
const DESCRIPTION = { md: "body-b2", sm: "body-b3" } as const;

export interface PanelProps {
  /** The panel's heading; it names the region. Leave it out for a bare surface. */
  title?: string;
  /** A word above the title: "Step 3". Plain text, outside the heading. */
  eyebrow?: string;
  /** One line under the title. */
  description?: string;
  /** The heading's level, from the page outline. */
  headingLevel?: "h2" | "h3";
  /** Something on the title's line, at its end: an action, a filter, a total. */
  end?: ReactNode;
  /** `sm` is tighter, with a smaller heading, for panels that share the page. */
  size?: "sm" | "md";
  children: ReactNode;
  className?: string;
}

/**
 * A plain surface that holds one part of a page: a settings panel, a form, a summary, a step. With
 * a title it is a region named by it; without one, just the surface.
 */
export function Panel({
  title,
  eyebrow,
  description,
  headingLevel = "h2",
  end,
  size = "md",
  children,
  className,
}: PanelProps) {
  const id = useId();
  const Root = title ? "section" : "div";
  return (
    <Card
      radius="2xl"
      render={<Root aria-labelledby={title ? id : undefined} />}
      className={panelStyles({ size, className })}
    >
      {title && (
        <div className={cn("flex items-start justify-between gap-3", size === "md" && "flex-wrap")}>
          <div className="flex min-w-0 flex-col gap-1">
            {eyebrow && (
              <Text as="p" variant="caption-eyebrow" tone="accent">
                {eyebrow}
              </Text>
            )}
            <Text as={headingLevel} id={id} variant={HEADING[size]} tone="primary">
              {title}
            </Text>
            {description && (
              <Text as="p" variant={DESCRIPTION[size]} tone="secondary">
                {description}
              </Text>
            )}
          </div>
          {end}
        </div>
      )}
      {children}
    </Card>
  );
}
